import { ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileUpload } from "@/components/course/FileUpload";
import { VideoUpload } from "@/components/course/VideoUpload";

export const RowControls = ({ index, total, onMove, onRemove, testId }) => (
  <div className="flex gap-1">
    <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint disabled:opacity-30" data-testid={`${testId}-up`}><ArrowUp className="h-4 w-4" /></button>
    <button type="button" disabled={index === total - 1} onClick={() => onMove(1)} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint disabled:opacity-30" data-testid={`${testId}-down`}><ArrowDown className="h-4 w-4" /></button>
    <button type="button" onClick={onRemove} className="rounded-md p-2 text-zinc-500 hover:bg-rose-50 hover:text-rose-600" data-testid={`${testId}-remove`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

export function LessonEditor({ lesson, index, total, onChange, onMove, onRemove }) {
  const t = `builder-lesson-${index + 1}`;
  const set = (k, v) => onChange({ ...lesson, [k]: v });
  return (
    <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={t}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-lasso-ink font-display text-xs font-bold text-white">{index + 1}</span>
          <span className="rounded-full bg-lasso-tint px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-lasso-deep">{lesson.type}</span>
        </div>
        <RowControls index={index} total={total} onMove={onMove} onRemove={onRemove} testId={t} />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_110px]">
        <Input placeholder="Lesson title" value={lesson.title} onChange={(e) => set("title", e.target.value)} data-testid={`${t}-title`} />
        <Input type="number" min={1} value={lesson.minutes} onChange={(e) => set("minutes", Number(e.target.value) || 1)} data-testid={`${t}-minutes`} aria-label="Minutes" />
      </div>
      {lesson.type === "video" && (
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">YouTube or Vimeo URL</Label>
            <Input placeholder="https://www.youtube.com/watch?v=…" value={lesson.video_url || ""} onChange={(e) => onChange({ ...lesson, video_url: e.target.value, file_id: e.target.value ? null : lesson.file_id })} data-testid={`${t}-video-url`} />
          </div>
          <p className="text-xs text-muted-foreground">— or upload your own video (MP4, M4V or MOV) —</p>
          <VideoUpload fileName={lesson.file_id && lesson.file_name} testId={`${t}-video-upload`} onUploaded={(f) => onChange({ ...lesson, file_id: f.id, file_name: f.original_filename, video_url: null })} />
        </div>
      )}
      {lesson.type === "pdf" && (
        <div className="mt-4">
          <FileUpload accept="application/pdf" label={lesson.file_id ? "Replace PDF" : "Upload PDF"} fileName={lesson.file_id && (lesson.file_name || "Uploaded PDF")} testId={`${t}-pdf-upload`} onUploaded={(f) => onChange({ ...lesson, file_id: f.id, file_name: f.original_filename })} />
        </div>
      )}
      <div className="mt-4 space-y-2">
        <Label className="text-xs">{lesson.type === "text" ? "Content (Markdown)" : "Notes (Markdown, optional)"}</Label>
        <Textarea rows={lesson.type === "text" ? 8 : 3} className="font-mono text-xs" value={lesson.content} onChange={(e) => set("content", e.target.value)} placeholder={"## Heading\n- bullet\n**bold**"} data-testid={`${t}-content`} />
      </div>
    </div>
  );
}
